import React from 'react';
import { Link } from 'react-router-dom';
import './CertificateDetailTemplate.css';

// Academic 3D Graduation Cap Illustration with floating sparkles
const AcademicCapIllustration = () => (
  <div className="cert-academic-cap-wrapper" aria-hidden="true">
    <svg
      viewBox="0 0 280 240"
      className="cert-academic-cap-svg"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id="capShadow" x="-20%" y="-20%" width="150%" height="150%">
          <feDropShadow dx="0" dy="18" stdDeviation="16" floodColor="#0f172a" floodOpacity="0.28" />
        </filter>
        <filter id="capGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="14" result="blur" />
        </filter>
        <linearGradient id="capTopGrad" x1="40" y1="50" x2="220" y2="150" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="45%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>
        <linearGradient id="capHighlight" x1="50" y1="50" x2="160" y2="120" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#475569" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#1e293b" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="tasselGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="50%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
        <linearGradient id="skullCapGrad" x1="70" y1="120" x2="170" y2="190" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#090d16" />
        </linearGradient>
      </defs>

      {/* Ambient Glow */}
      <ellipse cx="140" cy="140" rx="90" ry="40" fill="#38bdf8" opacity="0.25" filter="url(#capGlow)" />

      {/* Floating Sparkles */}
      <path
        d="M 60 45 Q 60 55 70 55 Q 60 55 60 65 Q 60 55 50 55 Q 60 55 60 45 Z"
        fill="#38bdf8"
        className="cert-sparkle-1"
      />
      <path
        d="M 85 25 Q 85 32 92 32 Q 85 32 85 39 Q 85 32 78 32 Q 85 32 85 25 Z"
        fill="#fbbf24"
        className="cert-sparkle-2"
      />
      <path
        d="M 235 65 Q 235 73 243 73 Q 235 73 235 81 Q 235 73 227 73 Q 235 73 235 65 Z"
        fill="#818cf8"
        className="cert-sparkle-3"
      />

      {/* 3D Mortarboard Assembly */}
      <g filter="url(#capShadow)">
        {/* Skull cap base under mortarboard */}
        <path
          d="M 85 125 C 85 125, 92 180, 142 185 C 192 180, 200 125, 200 125 C 200 145, 185 178, 142 178 C 98 178, 85 145, 85 125 Z"
          fill="url(#skullCapGrad)"
        />
        <ellipse cx="142" cy="158" rx="46" ry="12" fill="#090d16" opacity="0.6" />

        {/* 3D Diamond Mortarboard Upper Plate */}
        <path
          d="M 140 52 L 236 102 L 140 148 L 44 98 Z"
          fill="url(#capTopGrad)"
          stroke="#334155"
          strokeWidth="1.5"
        />

        {/* Glossy Bevel Highlight on Rim */}
        <path
          d="M 44 98 L 140 148 L 140 152 L 44 102 Z"
          fill="#0f172a"
        />
        <path
          d="M 236 102 L 140 148 L 140 152 L 236 106 Z"
          fill="#090d16"
        />
        <path
          d="M 46 97 L 140 146"
          stroke="url(#capHighlight)"
          strokeWidth="2"
        />

        {/* Cap Center Button */}
        <ellipse cx="140" cy="100" rx="7" ry="4" fill="#334155" />
        <ellipse cx="140" cy="99" rx="5" ry="3" fill="#64748b" />

        {/* Gold Tassel Band */}
        <path
          d="M 140 100 Q 185 96 195 125 Q 200 145 204 165"
          fill="none"
          stroke="url(#tasselGrad)"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        {/* Tassel Ring / Accent */}
        <circle cx="204" cy="165" r="4.5" fill="#fde047" stroke="#b45309" strokeWidth="1" />
        {/* Dangling Tassel Fringes */}
        <path
          d="M 201 168 L 198 198 L 210 198 L 207 168 Z"
          fill="url(#tasselGrad)"
        />
        <line x1="204" y1="172" x2="204" y2="198" stroke="#fef08a" strokeWidth="1" />
      </g>
    </svg>
  </div>
);

// Academic Laurel & Medal Watermark (Top Right)
const LaurelWatermark = () => (
  <div className="cert-laurel-watermark" aria-hidden="true">
    <svg viewBox="0 0 200 200" className="cert-laurel-svg" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Laurel Leaves Left */}
      <path
        d="M 68 152 C 55 138, 48 116, 50 94 C 52 74, 62 56, 78 44 C 76 56, 68 68, 68 84 C 68 100, 75 118, 88 132 Z"
        fill="currentColor"
      />
      <path
        d="M 45 88 C 36 82, 38 68, 48 64 C 54 72, 52 82, 45 88 Z"
        fill="currentColor"
      />
      <path
        d="M 52 118 C 42 114, 42 100, 52 94 C 58 102, 58 112, 52 118 Z"
        fill="currentColor"
      />
      <path
        d="M 66 144 C 56 142, 54 128, 64 122 C 72 128, 72 138, 66 144 Z"
        fill="currentColor"
      />

      {/* Laurel Leaves Right */}
      <path
        d="M 132 152 C 145 138, 152 116, 150 94 C 148 74, 138 56, 122 44 C 124 56, 132 68, 132 84 C 132 100, 125 118, 112 132 Z"
        fill="currentColor"
      />
      <path
        d="M 155 88 C 164 82, 162 68, 152 64 C 146 72, 148 82, 155 88 Z"
        fill="currentColor"
      />
      <path
        d="M 148 118 C 158 114, 158 100, 148 94 C 142 102, 142 112, 148 118 Z"
        fill="currentColor"
      />
      <path
        d="M 134 144 C 144 142, 146 128, 136 122 C 128 128, 128 138, 134 144 Z"
        fill="currentColor"
      />

      {/* Central Star Seal */}
      <circle cx="100" cy="98" r="32" stroke="currentColor" strokeWidth="2.5" strokeDasharray="4 2" />
      <polygon
        points="100,78 105,90 118,92 108,101 111,114 100,107 89,114 92,101 82,92 95,90"
        fill="currentColor"
      />

      {/* Bottom Ribbon Knot */}
      <path
        d="M 88 152 Q 100 162 112 152 Q 108 175 96 182 Q 104 175 88 152 Z"
        fill="currentColor"
      />
      <path
        d="M 112 152 Q 100 162 88 152 Q 92 175 104 182 Q 96 175 112 152 Z"
        fill="currentColor"
      />
    </svg>
  </div>
);

// Dot Matrix Grid
const DotMatrix = ({ className = '' }) => (
  <div className={`cert-dot-matrix ${className}`} aria-hidden="true">
    {[...Array(20)].map((_, i) => (
      <span key={i} className="cert-matrix-dot" />
    ))}
  </div>
);

// Dynamic Category Icon for Card Header
const CategoryIcon = ({ type = 'code' }) => {
  switch (type) {
    case 'ai':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'cloud':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
        </svg>
      );
    case 'database':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
          <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3" />
        </svg>
      );
    case 'design':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
          <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
          <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
          <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.563-2.512 5.563-5.563C22 6.5 17.5 2 12 2Z" />
        </svg>
      );
    case 'web':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      );
    case 'document':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    case 'code':
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
      );
  }
};

// Issuer Building SVG Icon
const BuildingIcon = () => (
  <svg className="cert-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="21" x2="21" y2="21" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <polyline points="5 6 12 3 19 6" />
    <line x1="4" y1="10" x2="4" y2="21" />
    <line x1="20" y1="10" x2="20" y2="21" />
    <line x1="8" y1="14" x2="8" y2="17" />
    <line x1="12" y1="14" x2="12" y2="17" />
    <line x1="16" y1="14" x2="16" y2="17" />
  </svg>
);

// Calendar SVG Icon
const CalendarIcon = () => (
  <svg className="cert-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Document with lines SVG Icon
const DocumentActionIcon = () => (
  <svg className="cert-btn-icon-left" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" fillOpacity="0.3" />
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <line x1="8" y1="13" x2="16" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="8" y1="17" x2="13" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// External Link ↗ SVG Icon
const ExternalLinkIcon = () => (
  <svg className="cert-btn-icon-right" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

// Summary Metric Icons
const MedalMetricIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" />
    <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);

const BookMetricIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z" />
    <path d="M6 6h10" />
    <path d="M6 10h10" />
  </svg>
);

const ChartMetricIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
    <line x1="2" y1="20" x2="22" y2="20" />
  </svg>
);

const StarMetricIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// Determine category icon type based on certificate title
const resolveCategoryType = (title = '', index = 0) => {
  const lower = title.toLowerCase();
  if (lower.includes('ai') || lower.includes('machine learning') || lower.includes('gemini') || lower.includes('prompt')) {
    return 'ai';
  }
  if (lower.includes('cloud') || lower.includes('aws') || lower.includes('azure')) {
    return 'cloud';
  }
  if (lower.includes('ux') || lower.includes('ui') || lower.includes('design') || lower.includes('canva')) {
    return 'design';
  }
  if (lower.includes('data') || lower.includes('sql') || lower.includes('analyst')) {
    return 'database';
  }
  if (lower.includes('web') || lower.includes('html') || lower.includes('css') || lower.includes('react') || lower.includes('express')) {
    return 'web';
  }
  if (lower.includes('java') || lower.includes('software') || lower.includes('engineer') || lower.includes('manager')) {
    return 'code';
  }
  const defaults = ['document', 'code', 'ai', 'cloud', 'database', 'web', 'design'];
  return defaults[index % defaults.length];
};

// Card accent color styles
const cardThemes = [
  { pillClass: 'theme-pill-blue', iconClass: 'theme-icon-blue' },
  { pillClass: 'theme-pill-green', iconClass: 'theme-icon-green' },
  { pillClass: 'theme-pill-amber', iconClass: 'theme-icon-amber' },
  { pillClass: 'theme-pill-purple', iconClass: 'theme-icon-purple' },
  { pillClass: 'theme-pill-cyan', iconClass: 'theme-icon-cyan' },
  { pillClass: 'theme-pill-rose', iconClass: 'theme-icon-rose' },
];

// Extract highest year from certificates or fallback
const extractLatestYear = (certs = []) => {
  let highest = 0;
  certs.forEach((c) => {
    if (c.date) {
      const match = c.date.match(/\b(20\d{2})\b/);
      if (match) {
        const yr = parseInt(match[1], 10);
        if (yr > highest) highest = yr;
      }
    }
  });
  return highest > 0 ? highest : '2025';
};

// Count distinct skill areas from certificates
const calculateSkillCount = (certs = []) => {
  if (!certs || certs.length === 0) return 0;
  const uniqueTitles = new Set(certs.map((c) => c.title.trim().toLowerCase()));
  return uniqueTitles.size;
};

// Render Dynamic Page Heading with brand-specific styling
const RenderHeading = ({ provider = '', customHeading = null }) => {
  if (customHeading) {
    return <h1 className="cert-page-heading">{customHeading}</h1>;
  }

  const pLower = provider.toLowerCase().trim();

  // Special match for Infosys Springboard to match reference image precisely
  if (pLower.includes('infosys') && pLower.includes('springboard')) {
    return (
      <h1 className="cert-page-heading">
        <span>My </span>
        <span className="brand-infosys-word">Infosys </span>
        <span className="brand-springboard-word">Springboard </span>
        <span>Certifications</span>
      </h1>
    );
  }

  // Brand-specific gradient classes
  let brandClass = 'brand-universal';
  if (pLower.includes('google')) brandClass = 'brand-google';
  else if (pLower.includes('aws') || pLower.includes('amazon')) brandClass = 'brand-aws';
  else if (pLower.includes('microsoft')) brandClass = 'brand-microsoft';
  else if (pLower.includes('guvi')) brandClass = 'brand-guvi';
  else if (pLower.includes('hackerrank') || pLower.includes('hacker rank')) brandClass = 'brand-hackerrank';
  else if (pLower.includes('great learning')) brandClass = 'brand-greatlearning';
  else if (pLower.includes('udemy')) brandClass = 'brand-udemy';
  else if (pLower.includes('linkedin')) brandClass = 'brand-linkedin';
  else if (pLower.includes('roadmap')) brandClass = 'brand-roadmap';
  else if (pLower.includes('webinar')) brandClass = 'brand-webinar';

  return (
    <h1 className="cert-page-heading">
      <span>My </span>
      <span className={`brand-text-gradient ${brandClass}`}>{provider} </span>
      <span>Certifications</span>
    </h1>
  );
};

/**
 * Reusable Certificate Detail Page Template
 * Supports all existing and future certificate detail pages.
 */
const CertificateDetailTemplate = ({
  provider = 'Platform',
  certificates = [],
  customHeading = null,
  subtitle = null,
  featureBadges = [
    { icon: '🏆', text: 'Industry Aligned' },
    { icon: '📊', text: 'Skill Development' },
    { icon: '📖', text: 'Continuous Learning' }
  ]
}) => {
  const latestYear = extractLatestYear(certificates);
  const skillCount = calculateSkillCount(certificates);
  const certCount = certificates.length;

  const displaySubtitle =
    subtitle || `Here are the ${provider} certifications I have completed as part of my learning journey.`;

  React.useEffect(() => {
    const headingText = customHeading || `My ${provider} Certifications`;
    document.title = `${headingText} | Praveen Kumar B`;
  }, [provider, customHeading]);

  return (
    <div className="cert-detail-page">
      {/* Background Ambient Glow Orbs */}
      <div className="cert-ambient-glows" aria-hidden="true">
        <div className="cert-glow-orb glow-top-left" />
        <div className="cert-glow-orb glow-top-right" />
        <div className="cert-glow-orb glow-center-purple" />
        <div className="cert-glow-orb glow-bottom-cyan" />
      </div>

      {/* Decorative Corner Watermarks & Illustrations */}
      <AcademicCapIllustration />
      <LaurelWatermark />
      <DotMatrix className="matrix-left" />
      <DotMatrix className="matrix-right" />

      <div className="cert-detail-inner-container">
        {/* Navigation Breadcrumb / Back Link */}
        <div className="cert-nav-top">
          <Link to="/certificates" className="cert-back-btn">
            <span className="cert-back-arrow">←</span>
            <span>All Certifications</span>
          </Link>
        </div>

        {/* 1. HERO / HEADER SECTION */}
        <header className="cert-hero-header">
          {/* Top Pill Badge */}
          <div className="cert-top-pill">
            <span className="cert-pill-icon">🎓</span>
            <span className="cert-pill-text">CERTIFICATIONS</span>
          </div>

          {/* Dynamic Gradient Heading */}
          <RenderHeading provider={provider} customHeading={customHeading} />

          {/* Subtitle */}
          <p className="cert-hero-subtitle">{displaySubtitle}</p>

          {/* Feature Badges Row */}
          {featureBadges && featureBadges.length > 0 && (
            <div className="cert-feature-badges-row">
              {featureBadges.map((badge, idx) => (
                <div key={idx} className={`cert-feature-pill pill-style-${idx % 3}`}>
                  <span className="badge-pill-icon">{badge.icon}</span>
                  <span className="badge-pill-label">{badge.text}</span>
                </div>
              ))}
            </div>
          )}
        </header>

        {/* 2. CERTIFICATE CARDS GRID */}
        <main className="cert-cards-section">
          <div className="cert-cards-grid">
            {certificates.map((cert, index) => {
              const theme = cardThemes[index % cardThemes.length];
              const categoryType = resolveCategoryType(cert.title, index);
              const cardIndexLabel = `#${index + 1}`;

              return (
                <article className="cert-card-item" key={index}>
                  {/* Card Header: Icon Badge & Index Number */}
                  <div className="cert-card-header">
                    <div className={`cert-category-badge ${theme.iconClass}`} title={categoryType}>
                      <CategoryIcon type={categoryType} />
                    </div>
                    <span className={`cert-index-pill ${theme.pillClass}`}>
                      {cardIndexLabel}
                    </span>
                  </div>

                  {/* Provider Logo Container */}
                  <div className="cert-logo-container">
                    <img
                      src={cert.image}
                      alt={cert.title || `${provider} Certificate`}
                      className="cert-provider-logo"
                      loading="lazy"
                    />
                  </div>

                  {/* Certificate Title */}
                  <h2 className="cert-item-title">{cert.title}</h2>

                  {/* Metadata Rows */}
                  <div className="cert-metadata-container">
                    <div className="cert-meta-row">
                      <BuildingIcon />
                      <span className="cert-meta-label">Issued By:</span>
                      <strong className="cert-meta-value">{cert.issuedBy || provider}</strong>
                    </div>

                    {cert.date && (
                      <div className="cert-meta-row">
                        <CalendarIcon />
                        <span className="cert-meta-label">Date:</span>
                        <strong className="cert-meta-value">{cert.date}</strong>
                      </div>
                    )}

                    {cert.credentialId && (
                      <div className="cert-meta-row">
                        <span className="cert-meta-icon-text">#</span>
                        <span className="cert-meta-label">ID:</span>
                        <strong className="cert-meta-value">{cert.credentialId}</strong>
                      </div>
                    )}
                  </div>

                  {/* Action Button: View Certificate */}
                  <div className="cert-action-wrapper">
                    <a
                      href={cert.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cert-view-btn"
                    >
                      <DocumentActionIcon />
                      <span className="cert-btn-text">→ View Certificate</span>
                      <ExternalLinkIcon />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        </main>

        {/* 3. CERTIFICATION SUMMARY BANNER */}
        <section className="cert-summary-section">
          <div className="cert-summary-card">
            {/* Metric 1: Total Completed */}
            <div className="cert-summary-item">
              <div className="cert-metric-icon-circle icon-circle-blue">
                <MedalMetricIcon />
              </div>
              <div className="cert-metric-text-group">
                <span className="cert-metric-number">{certCount}</span>
                <span className="cert-metric-primary-label">Certifications</span>
                <span className="cert-metric-secondary-label">Completed</span>
              </div>
            </div>

            {/* Metric 2: Skill Areas */}
            <div className="cert-summary-item">
              <div className="cert-metric-icon-circle icon-circle-green">
                <BookMetricIcon />
              </div>
              <div className="cert-metric-text-group">
                <span className="cert-metric-number">{skillCount}</span>
                <span className="cert-metric-primary-label">Skill Areas</span>
                <span className="cert-metric-secondary-label">Explored</span>
              </div>
            </div>

            {/* Metric 3: Platform */}
            <div className="cert-summary-item">
              <div className="cert-metric-icon-circle icon-circle-purple">
                <ChartMetricIcon />
              </div>
              <div className="cert-metric-text-group">
                <span className="cert-metric-platform-name">{provider}</span>
                <span className="cert-metric-primary-label">Issued By</span>
                <span className="cert-metric-secondary-label">Platform</span>
              </div>
            </div>

            {/* Metric 4: Latest Year */}
            <div className="cert-summary-item">
              <div className="cert-metric-icon-circle icon-circle-amber">
                <StarMetricIcon />
              </div>
              <div className="cert-metric-text-group">
                <span className="cert-metric-number">{latestYear}</span>
                <span className="cert-metric-primary-label">Latest Certification</span>
                <span className="cert-metric-secondary-label">Year</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CertificateDetailTemplate;
